/**
 * Outing API Hook - All API calls for outing management
 * Modular and extractable for standalone app use.
 */
import { useCallback } from 'react';

export function useOutingApi(apiBaseUrl) {
  const API_BASE = apiBaseUrl || process.env.REACT_APP_BACKEND_URL;

  // Helper for API calls
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const url = `${API_BASE}/api/outings${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `API error: ${response.status}`);
    }
    
    return response.json();
  }, [API_BASE]);

  // ==================== OUTINGS ====================

  const createOuting = useCallback(async (outingData, userId, userName) => {
    return apiCall(`/?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, {
      method: 'POST',
      body: JSON.stringify(outingData)
    });
  }, [apiCall]);

  const listOutings = useCallback(async (filters = {}) => {
    const params = new URLSearchParams(filters);
    return apiCall(`/?${params}`);
  }, [apiCall]);

  const getOuting = useCallback(async (outingId) => {
    return apiCall(`/${outingId}`);
  }, [apiCall]);

  const updateOuting = useCallback(async (outingId, updates, userId) => {
    return apiCall(`/${outingId}?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }, [apiCall]);

  const updateOutingStatus = useCallback(async (outingId, status, userId) => {
    return apiCall(`/${outingId}/status?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify({ status })
    });
  }, [apiCall]);

  // ==================== INVITATIONS ====================

  const sendInvitation = useCallback(async (outingId, invitation, userId) => {
    return apiCall(`/${outingId}/invitations?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(invitation)
    });
  }, [apiCall]);

  const sendBulkInvitations = useCallback(async (outingId, invitations, userId) => {
    return apiCall(`/${outingId}/invitations/bulk?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(invitations)
    });
  }, [apiCall]);

  const listInvitations = useCallback(async (outingId, status = null) => {
    let url = `/${outingId}/invitations`;
    if (status) url += `?status=${status}`;
    return apiCall(url);
  }, [apiCall]);

  const respondToInvitation = useCallback(async (outingId, invitationId, response, userId = null) => {
    let url = `/${outingId}/invitations/${invitationId}/respond`;
    if (userId) url += `?user_id=${userId}`;
    return apiCall(url, {
      method: 'POST',
      body: JSON.stringify({ response })
    });
  }, [apiCall]);

  // ==================== TEAMS ====================

  const createTeam = useCallback(async (outingId, teamData, userId) => {
    return apiCall(`/${outingId}/teams?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(teamData)
    });
  }, [apiCall]);

  const listTeams = useCallback(async (outingId) => {
    return apiCall(`/${outingId}/teams`);
  }, [apiCall]);

  const updateTeam = useCallback(async (outingId, teamId, updates, userId) => {
    return apiCall(`/${outingId}/teams/${teamId}?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }, [apiCall]);

  const autoPairPlayers = useCallback(async (outingId, userId) => {
    return apiCall(`/${outingId}/teams/auto-pair?user_id=${userId}`, {
      method: 'POST'
    });
  }, [apiCall]);

  // ==================== PAYMENTS ====================

  const recordPayment = useCallback(async (outingId, paymentData, userId, userName) => {
    return apiCall(`/${outingId}/payments?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }, [apiCall]);

  const listPayments = useCallback(async (outingId) => {
    return apiCall(`/${outingId}/payments`);
  }, [apiCall]);

  // ==================== SCORECARDS ====================

  const submitScorecard = useCallback(async (outingId, scorecardData, userId) => {
    return apiCall(`/${outingId}/scorecards?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(scorecardData)
    });
  }, [apiCall]);

  const listScorecards = useCallback(async (outingId, status = null) => {
    let url = `/${outingId}/scorecards`;
    if (status) url += `?status=${status}`;
    return apiCall(url);
  }, [apiCall]);

  const approveScorecard = useCallback(async (outingId, scorecardId, userId, userName) => {
    return apiCall(`/${outingId}/scorecards/${scorecardId}/approve?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, {
      method: 'POST'
    });
  }, [apiCall]);

  const flagScorecard = useCallback(async (outingId, scorecardId, reason, userId) => {
    return apiCall(`/${outingId}/scorecards/${scorecardId}/flag?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }, [apiCall]);

  // ==================== CTP ====================

  const submitCTPEntry = useCallback(async (outingId, ctpData, userId) => {
    return apiCall(`/${outingId}/ctp?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(ctpData)
    });
  }, [apiCall]);

  const getCTPLeaderboard = useCallback(async (outingId) => {
    return apiCall(`/${outingId}/ctp`);
  }, [apiCall]);

  const calculatePinDistance = useCallback(async (outingId, imageBase64, userId) => {
    return apiCall(`/${outingId}/ctp/calculate-distance?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify({ image_base64: imageBase64 })
    });
  }, [apiCall]);

  // ==================== LEADERBOARD ====================

  const getLeaderboard = useCallback(async (outingId) => {
    return apiCall(`/${outingId}/leaderboard`);
  }, [apiCall]);

  // ==================== NOTIFICATIONS ====================

  const broadcastNotification = useCallback(async (outingId, notificationData, userId) => {
    return apiCall(`/${outingId}/notify?user_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(notificationData)
    });
  }, [apiCall]);

  const getNotifications = useCallback(async (outingId, since = null, limit = 20) => {
    let url = `/${outingId}/notifications?limit=${limit}`;
    if (since) url += `&since=${encodeURIComponent(since)}`;
    return apiCall(url);
  }, [apiCall]);

  return {
    // Outings
    createOuting,
    listOutings,
    getOuting,
    updateOuting,
    updateOutingStatus,
    
    // Invitations
    sendInvitation,
    sendBulkInvitations,
    listInvitations,
    respondToInvitation,
    
    // Teams
    createTeam,
    listTeams,
    updateTeam,
    autoPairPlayers,
    
    // Payments
    recordPayment,
    listPayments,
    
    // Scorecards
    submitScorecard,
    listScorecards,
    approveScorecard,
    flagScorecard,
    
    // CTP
    submitCTPEntry,
    getCTPLeaderboard,
    calculatePinDistance,
    
    // Leaderboard
    getLeaderboard,
    
    // Notifications
    broadcastNotification,
    getNotifications
  };
}
